/** @file yawPitchRollOf: quatFromYawPitchRoll inverted, the reading nearest a hint, each in (-180, 180]. */
import type { Quat } from '@zilo/pose3d-math/quat/quat';
import { quatFromYawPitchRoll } from '@zilo/pose3d-math/quat/quatFromYawPitchRoll';
import { quatMul } from '@zilo/pose3d-math/quat/quatMul';
import { eulerYawPitchOf } from '@zilo/pose3d-math/quat/eulerYawPitchOf';
import { quatConjugate } from './quatConjugate';
import { shortestYawDelta } from './shortestYawDelta';

const DEG = 180 / Math.PI;

type Ypr = [number, number, number];

/** Sum of the shortest distances of the three angles. */
const distance = (a: Ypr, b: Ypr): number =>
  Math.abs(shortestYawDelta(a[0], b[0])) + Math.abs(shortestYawDelta(a[1], b[1])) + Math.abs(shortestYawDelta(a[2], b[2]));

/**
 * `[yaw, pitch, roll]` with `quatFromYawPitchRoll(...) = q`: yaw and pitch from math's `eulerYawPitchOf`, roll from the
 * remaining turn about the forward axis. Of the two readings of every rotation (y, p, r) and (y + 180, 180 - p,
 * r + 180) the one nearest `hint` is returned (a drag keeps its numbers continuous), each wrapped into (-180, 180].
 */
export function yawPitchRollOf(q: Quat, hint: Ypr = [0, 0, 0]): Ypr {
  // The Euler inverse, not the facet read-back of design 21.1 (which folds roll into yaw and pitch).
  const [yaw, pitch] = eulerYawPitchOf(q);
  const rest = quatMul(quatConjugate(quatFromYawPitchRoll(yaw, pitch, 0)), q);
  const roll = 2 * Math.atan2(-rest[1], rest[3]) * DEG;
  const wrap = (a: number): number => shortestYawDelta(0, a);
  const one: Ypr = [wrap(yaw), wrap(pitch), wrap(roll)];
  const two: Ypr = [wrap(yaw + 180), wrap(180 - pitch), wrap(roll + 180)];
  return distance(two, hint) < distance(one, hint) ? two : one;
}
